import {
  armHidWedge,
  disarmHidWedge,
  ingestCaptureText,
  injectTag,
  isHidWedgeArmed,
  subscribeKeyActivity,
  subscribeTagReads as subscribeWedge,
  type KeyActivityHandler,
  type TagReadHandler,
} from './hidWedgeScanner'
import {
  createScannerAdapter,
  getScannerAdapter,
  isNativeBridgeAvailable,
  subscribeNativeTag,
  type ScannerType as FacadeScannerType,
} from './scanner'
import {
  useRfidSessionStore,
  type ScannerType,
} from '../stores/rfidSessionStore'

export type { TagReadHandler, KeyActivityHandler }
export { ingestCaptureText, subscribeKeyActivity, isNativeBridgeAvailable }

let verifyUnsub: (() => void) | null = null
let nativeConnUnsub: (() => void) | null = null
let nativeVerifyUnsub: (() => void) | null = null

function ensureVerifyOnFirstTag() {
  if (verifyUnsub) return
  verifyUnsub = subscribeWedge(() => {
    useRfidSessionStore.getState().confirmScannerLink()
  })
}

function ensureNativeSubscriptions() {
  if (!isNativeBridgeAvailable()) return
  if (!nativeConnUnsub) {
    const adapter = getScannerAdapter()
    nativeConnUnsub = adapter.subscribeConnection((ev) => {
      const store = useRfidSessionStore.getState()
      if (ev.connected) {
        const type = (String(ev.type || '').toUpperCase() === 'RFD40'
          ? 'RFD40'
          : 'RFD8500') as ScannerType
        store.applySelection(type, { rfid_device: ev.rfid_device || type })
        store.confirmScannerLink()
      } else {
        store.clearConnection()
      }
    })
  }
  if (!nativeVerifyUnsub) {
    nativeVerifyUnsub = subscribeNativeTag(() => {
      useRfidSessionStore.getState().confirmScannerLink()
    })
  }
}

export function isMockScannerEnabled(): boolean {
  return (
    String(import.meta.env.VITE_MOCK_SCANNER || '').trim() === 'true' ||
    String(import.meta.env.VITE_MOCK_SCANNER || '').trim() === '1'
  )
}

/**
 * Select a machine and arm HID capture.
 * Does NOT mark the scanner as connected until a physical tag is received.
 */
export async function connectHidWedge(
  type: ScannerType = 'RFD8500',
  machineCode?: string
): Promise<void> {
  const name = machineCode?.trim() || type
  armHidWedge()
  ensureVerifyOnFirstTag()
  useRfidSessionStore.getState().applySelection(type, {
    rfid_device: name,
  })
}

/** Native SDK connect (RFID Connector WebView). */
export async function connectNative(
  type: ScannerType,
  name?: string,
  address?: string
): Promise<void> {
  if (!isNativeBridgeAvailable()) {
    throw new Error('RFID Connector native bridge not available')
  }
  ensureNativeSubscriptions()
  const adapter = createScannerAdapter()
  useRfidSessionStore.getState().applySelection(type, {
    rfid_device: name?.trim() || type,
  })

  await new Promise<void>((resolve, reject) => {
    const timeoutMs = 25_000
    let settled = false
    const finish = (fn: () => void) => {
      if (settled) return
      settled = true
      clearTimeout(timer)
      unsubConn()
      unsubErr()
      fn()
    }
    const timer = setTimeout(() => {
      finish(() =>
        reject(
          new Error(
            'Scanner connect timed out. Power on the sled, close other RFID apps, then try again.'
          )
        )
      )
    }, timeoutMs)
    const unsubConn = adapter.subscribeConnection((ev) => {
      if (ev.connected) finish(() => resolve())
    })
    const unsubErr = adapter.subscribeError((ev) => {
      finish(() => reject(new Error(ev.message || 'Connect failed')))
    })
    void adapter.connect(type, name, address).catch((e) => {
      finish(() => reject(e instanceof Error ? e : new Error(String(e))))
    })
  })
}

export async function listNativeDevices(type: ScannerType) {
  ensureNativeSubscriptions()
  return getScannerAdapter().listDevices(type as FacadeScannerType)
}

export async function disconnect(): Promise<void> {
  if (isNativeBridgeAvailable()) {
    try {
      await getScannerAdapter().disconnect()
    } catch {
      /* ignore */
    }
  }
  disarmHidWedge()
  useRfidSessionStore.getState().clearConnection()
}

export function subscribeTagReads(handler: TagReadHandler): () => void {
  ensureVerifyOnFirstTag()
  ensureNativeSubscriptions()
  const unsubWedge = subscribeWedge(handler)
  let unsubNative: (() => void) | null = null
  if (isNativeBridgeAvailable()) {
    unsubNative = subscribeNativeTag((ev) => handler(String(ev.tagID).toUpperCase()))
  }
  return () => {
    unsubWedge()
    unsubNative?.()
  }
}

export function submitManualTag(tagId: string): void {
  ensureVerifyOnFirstTag()
  injectTag(tagId)
}

export function isScannerArmed(): boolean {
  return isHidWedgeArmed() || isNativeBridgeAvailable()
}

/** Soft-connect for mock/manual-only mode without requiring Connect click. */
export function ensureMockConnected(): void {
  if (!isMockScannerEnabled()) return
  const s = useRfidSessionStore.getState()
  if (!s.scannerConnected && !s.scannerPending) {
    armHidWedge()
    ensureVerifyOnFirstTag()
    s.applySelection('RFD8500', { rfid_device: 'Manual / Mock' })
  }
}
